import { create } from 'zustand';
import type { ImageFile, LLMConfig, ExifValue, StagedChange, LLMExifPayload } from '@/shared/types';
import type { UploadResult } from '@/shared/platform/types';
import { isElectron, platform } from '@/shared/platform';
import { getBackendUrl } from '@/shared/platform/web-adapter';

type LoggerMode = 'collapsed' | 'split' | 'maximized' | 'hidden';

interface ImageState {
  folderPath: string | null;
  instanceId: string | null;
  images: ImageFile[];
  activeImagePath: string | null;
  imageExif: Map<string, Record<string, unknown>>;
  selectedIndex: number | null;
  selectedIndices: Set<number>;
  searchQuery: string;
  stagedChanges: Map<string, Map<string, StagedChange>>;
  llmConfig: LLMConfig;
  showLLMPanel: boolean;
  showDiffPreview: boolean;
  llmLoading: boolean;
  llmLoadingPaths: Set<string>;
  debugLogs: Array<{ time: string; msg: string }>;
  updateableFields: Record<string, boolean>;
  loggerMode: LoggerMode;
  notifications: Array<{ type: 'info' | 'success' | 'warning' | 'error'; message: string }>;
  viewingImagePath: string | null;

  hasStagedChanges: (imagePath: string) => boolean;

  setActiveImagePath: (path: string | null) => void;
  setFolderPath: (path: string | null) => void;
  setInstanceId: (id: string | null) => void;
  setImages: (images: ImageFile[]) => void;
  setUploadedFiles: (result: UploadResult | null) => void;
  uploadFiles: (files: FileList) => Promise<void>;
  setExif: (path: string, exif: Record<string, unknown>) => void;
  setSelectedIndex: (idx: number | null) => void;
  toggleSelect: (idx: number, ctrlKey?: boolean, shiftKey?: boolean) => void;
  clearSelection: () => void;
  setSearchQuery: (q: string) => void;
  getFilteredImages: () => ImageFile[];
  stageChange: (imagePath: string, fieldKey: string, newValue: ExifValue, oldValue: ExifValue) => void;
  unstageChange: (imagePath: string, fieldKey: string) => void;
  resetStagedChanges: (imagePath?: string) => void;
  setLLMConfig: (config: Partial<LLMConfig>) => Promise<void>;
  loadLLMConfig: () => Promise<void>;
  setShowLLMPanel: (show: boolean) => void;
  setShowDiffPreview: (show: boolean) => void;
  generateLLMExif: (imagePath: string, silent?: boolean) => Promise<LLMExifPayload | null>;
  addLog: (msg: string) => void;
  setLLMLoading: (path: string, isLoading: boolean) => void;
  toggleFieldUpdate: (key: string, enabled: boolean) => void;
  applyLLMPayload: (imagePath: string, payload: LLMExifPayload) => void;
  addNotification: (type: 'info' | 'success' | 'warning' | 'error', message: string) => void;
  clearNotification: (idx: number) => void;
  getStagedForImage: (imagePath: string) => Map<string, StagedChange>;
  getChangesForPaths: (paths: string[]) => Array<{ path: string; tags: Record<string, unknown> }>;
  setLoggerMode: (mode: LoggerMode) => void;
  setViewingImagePath: (path: string | null) => void;
  generateBulkLLMExif: (paths: string[]) => Promise<void>;
}

export const useImageStore = create<ImageState>()((set, get) => ({
  folderPath: null,
  instanceId: (() => {
    // Stable instanceId: initialize once with localStorage persistence
    // to survive React remounts and page reloads
    try {
      const stored = localStorage.getItem('exif-studio-instance-id');
      if (stored && /^[a-f0-9-]{36}$/i.test(stored)) {
        return stored;
      }
    } catch {}
    const newId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
    try { localStorage.setItem('exif-studio-instance-id', newId); } catch {}
    return newId;
  })(),
  images: [],
  imageExif: new Map(),
  selectedIndex: null,
  activeImagePath: null,
  selectedIndices: new Set<number>(),
  searchQuery: '',
  stagedChanges: new Map(),
  llmConfig: { provider: 'openai', apiKey: '', model: 'gpt-4o', baseUrl: '', promptTemplate: '' },
  showLLMPanel: false,
  showDiffPreview: false,
  llmLoading: false,
  llmLoadingPaths: new Set(),
  debugLogs: [],
  loggerMode: 'collapsed',
  notifications: [],
  viewingImagePath: null,
  updateableFields: {
    Title: true, Description: true, Keywords: true,
    ImageWidth: false, ImageHeight: false, FileType: false, MIMEType: false,
    Make: false, Model: false, DateTimeOriginal: false, FNumber: false,
    ExposureTime: false, ISO: false, FocalLength: false,
    GPSLatitude: false, GPSLongitude: false, GPSPosition: false
  },

  hasStagedChanges: (imagePath: string): boolean => {
    const changes = get().stagedChanges.get(imagePath);
    return !!changes && changes.size > 0;
  },

  setActiveImagePath: (path) => set({ activeImagePath: path }),
  setFolderPath: (p: string | null) => set({ folderPath: p, activeImagePath: null, images: [] }),
  setInstanceId: (id) => {
    try { localStorage.setItem('exif-studio-instance-id', id || ''); } catch {}
    if (id && !isElectron && (platform as any).setInstanceId) {
      (platform as any).setInstanceId(id);
    }
    set({ instanceId: id });
  },
  setImages: (imgs) => { set({ images: imgs, imageExif: new Map(), stagedChanges: new Map(), selectedIndex: null, selectedIndices: new Set() }); },
  setUploadedFiles: (result) => {
    if (!result) return;
    set(() => ({
      folderPath: `/temp/${result.instanceId}`,
      instanceId: result.instanceId,
      images: result.files.map(f => {
        const instanceId = result.instanceId;
        const backendUrl = getBackendUrl();
        const apiThumb = `${backendUrl}/api/serve/${instanceId}/${f.name}`;
        return { name: f.name, path: f.path, size: f.size, exif: null, thumbnail: apiThumb };
      }),
      imageExif: new Map(),
      stagedChanges: new Map(),
      selectedIndex: null,
      selectedIndices: new Set()
    }));
  },
  uploadFiles: async (files) => {
    if (!files || files.length === 0) return;
    get().addLog(`Uploading ${files.length} file(s)...`);
    try {
      const result = await window.electronAPI.uploadFiles?.(files);
      if (result) {
        get().addLog(`Upload successful: ${result.files.length} file(s)`);
        get().setUploadedFiles(result);
      } else {
        get().addNotification('error', 'Upload failed');
        get().addLog('[ERROR] Upload failed');
      }
    } catch (e: any) {
      get().addNotification('error', 'Upload error: ' + e.message);
      get().addLog(`[ERROR] Upload error: ${e.message}\nStack: ${e.stack || 'N/A'}`);
    }
  },
  setExif: (imgPath, exif) => {
    set((state) => {
      const newMap = new Map(state.imageExif);
      newMap.set(imgPath, exif);
      return { imageExif: newMap };
    });
  },
  setSelectedIndex: (idx) => { set({ selectedIndex: idx }); },
  toggleSelect: (idx, ctrlKey, shiftKey) => {
    set((state) => {
      const newSel = new Set(state.selectedIndices);
      if (shiftKey && state.selectedIndex !== null) {
        const start = Math.min(state.selectedIndex, idx);
        const end = Math.max(state.selectedIndex, idx);
        for (let i = start; i <= end; i++) newSel.add(i);
      } else if (ctrlKey) {
        if (newSel.has(idx)) newSel.delete(idx); else newSel.add(idx);
      } else {
        newSel.clear();
        newSel.add(idx);
      }
      return { selectedIndices: newSel, selectedIndex: idx };
    });
  },
  clearSelection: () => { set({ selectedIndices: new Set(), selectedIndex: null }); },
  setSearchQuery: (q) => { set({ searchQuery: q }); },
  getFilteredImages: () => {
    const s = get();
    if (!s.searchQuery) return s.images;
    const q = s.searchQuery.toLowerCase();
    return s.images.filter((img) => img.name.toLowerCase().includes(q));
  },
  stageChange: (imagePath, fieldKey, newValue, oldValue) => {
    set((state) => {
      const map = new Map(state.stagedChanges);
      const inner = new Map(map.get(imagePath) || new Map());
      // Normalize empty values for comparison: treat null, undefined, '' as equivalent
      const normalize = (v: ExifValue) => (v === null || v === undefined || v === '') ? '' : v;
      if (normalize(newValue) === normalize(oldValue)) { inner.delete(fieldKey); } else {
        inner.set(fieldKey, { fieldKey, oldValue, newValue });
      }
      if (inner.size === 0) map.delete(imagePath); else map.set(imagePath, inner);
      return { stagedChanges: map };
    });
  },
  unstageChange: (imagePath, fieldKey) => {
    set((state) => {
      const map = new Map(state.stagedChanges);
      const inner = new Map(map.get(imagePath) || new Map());
      inner.delete(fieldKey);
      if (inner.size === 0) map.delete(imagePath); else map.set(imagePath, inner);
      return { stagedChanges: map };
    });
  },
  resetStagedChanges: (imagePath) => {
    set((state) => {
      if (imagePath) {
        const map = new Map(state.stagedChanges);
        map.delete(imagePath);
        return { stagedChanges: map };
      }
      return { stagedChanges: new Map() };
    });
  },
  setLLMConfig: async (partial) => {
    const newCfg = { ...get().llmConfig, ...partial };
    try {
      await window.electronAPI.invoke('llm:save-config', newCfg);
      set({ llmConfig: newCfg });
    } catch {}
  },
  loadLLMConfig: async () => {
    try {
      const cfg = await window.electronAPI.invoke('llm:get-config');
      if (cfg) set({ llmConfig: { ...get().llmConfig, ...cfg } });
    } catch {}
  },
  setShowLLMPanel: (show) => { set({ showLLMPanel: show }); },
  setShowDiffPreview: (show) => { set({ showDiffPreview: show }); },
  setLoggerMode: (mode) => set({ loggerMode: mode }),
  setViewingImagePath: (path) => set({ viewingImagePath: path }),
  generateLLMExif: async (imagePath, silent = false) => {
    set({ llmLoading: true });
    get().addLog(`LLM generation started for: ${imagePath}`);
    try {
      const cfg = get().llmConfig;
      const raw = await window.electronAPI.invoke(
        'llm:generate-exif', 
        { provider: cfg.provider, apiKey: cfg.apiKey, model: cfg.model, baseUrl: cfg.baseUrl, promptTemplate: cfg.promptTemplate },
        imagePath, 
        cfg.promptTemplate
      );

      if (raw) {
        if (typeof raw === 'object' && 'error' in raw) {
          get().addLog(`[ERROR] LLM Error: ${String((raw as any).error)}\nStack: ${(raw as any).stack || 'N/A'}`);
          if (!silent) get().addNotification('error', 'AI Error: ' + String((raw as any).error));
          return null;
        }
        let jsonString = typeof raw === 'string' ? raw : JSON.stringify(raw);
        try {
          jsonString = jsonString.replace(/^```json|```$/g, '').trim();
          const payload: LLMExifPayload = JSON.parse(jsonString);
          get().applyLLMPayload(imagePath, payload);
          get().addLog('LLM payload applied to form');
          return payload;
        } catch (e: any) {
          get().addLog(`[ERROR] Failed to parse LLM JSON: ${e.message}\nStack: ${e.stack || 'N/A'}`);
          if (!silent) get().addNotification('error', 'Invalid JSON from LLM');
        }
      }
      return null;
    } catch (e: any) {
      get().addLog(`[ERROR] LLM Generation failed: ${e.message}\nStack: ${e.stack || 'N/A'}`);
      if (!silent) get().addNotification('error', 'LLM Generation failed: ' + e.message);
      return null;
    } finally { set({ llmLoading: false }); }
  },
  generateBulkLLMExif: async (paths) => {
    const s = get();
    s.addNotification('info', `Starting bulk LLM generation for ${paths.length} image(s)...`);
    let success = 0; let failed = 0;
    for (const path of paths) {
      if (!get().llmLoadingPaths.has(path)) {
        get().setLLMLoading(path, true);
        try {
          const res = await s.generateLLMExif(path, true);
          if (res) success++; else failed++;
        } catch { failed++; } finally {
          get().setLLMLoading(path, false);
          set((state) => {
            const newSel = new Set(state.selectedIndices);
            const filtered = state.getFilteredImages();
            const idx = filtered.findIndex(img => img.path === path);
            if (idx !== -1) newSel.delete(idx);
            return { selectedIndices: newSel };
          });
        }
      }
    }
    s.addNotification('success', `Bulk LLM generation completed. Success: ${success}, Failed: ${failed}`);
  },
  addNotification: (type, message) => {
    set((state) => ({ notifications: [...state.notifications, { type, message }] }));
    if (type === 'error') {
      get().addLog(`[ERROR] ${message}`);
    } else if (type === 'warning') {
      get().addLog(`[WARN] ${message}`);
    }
  },
  clearNotification: (idx) => {
    set((state) => {
      const notifs = [...state.notifications];
      notifs.splice(idx, 1);
      return { notifications: notifs };
    });
  },
  addLog: (msg) => {
    set((state) => {
      const time = new Date().toLocaleTimeString();
      const newLogs = [{ time, msg }, ...state.debugLogs];
      return { debugLogs: newLogs.slice(0, 500) };
    });
  },
  setLLMLoading: (path, isLoading) => {
    set((state) => {
      const paths = new Set(state.llmLoadingPaths);
      if (isLoading) paths.add(path); else paths.delete(path);
      return { llmLoadingPaths: paths };
    });
  },
  toggleFieldUpdate: (key, enabled) => {
    set((state) => ({ updateableFields: { ...state.updateableFields, [key]: enabled } }));
  },
  getStagedForImage: (imagePath) => {
    return new Map(get().stagedChanges.get(imagePath) || new Map());
  },
  getChangesForPaths: (paths) => {
    const state = get();
    const uniquePaths = Array.from(new Set(paths.filter(Boolean)));

    return uniquePaths.reduce<Array<{ path: string; tags: Record<string, unknown> }>>((acc, imagePath) => {
      const stagedForImage = state.stagedChanges.get(imagePath);
      if (!stagedForImage || stagedForImage.size === 0) {
        return acc;
      }

      const tags: Record<string, unknown> = {};
      for (const [fieldKey, change] of stagedForImage.entries()) {
        if (!state.updateableFields[fieldKey]) {
          continue;
        }
        tags[fieldKey] = change.newValue;
      }

      if (Object.keys(tags).length > 0) {
        acc.push({ path: imagePath, tags });
      }

      return acc;
    }, []);
  },
  applyLLMPayload: (imagePath, payload) => {
    const state = get();
    const existingExif = state.imageExif.get(imagePath) || {};
    if (payload.title !== undefined && payload.title !== null) {
      state.stageChange(imagePath, 'Title', payload.title, (existingExif['Title'] ?? null) as ExifValue);
    }
    if (payload.description !== undefined && payload.description !== null) {
      state.stageChange(imagePath, 'Description', payload.description, (existingExif['Description'] ?? null) as ExifValue);
    }
    if (payload.keywords && payload.keywords.length > 0) {
      state.stageChange(imagePath, 'Keywords', payload.keywords.join(', '), (existingExif['Keywords'] ?? null) as ExifValue);
    }
  },
}));

export function addNotification(type: 'info' | 'success' | 'warning' | 'error', message: string) {
  useImageStore.getState().addNotification(type, message);
}

export function addLog(msg: string) {
  useImageStore.getState().addLog(msg);
}
